import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/CompleteAuthContext";
import Landing from "./components/Landing";
import Dashboard from "./components/Dashboard";
import AddExpense from "./components/AddExpense";
import CreateGroup from "./components/CreateGroup";
import GroupDetail from "./components/GroupDetail";
import SubscriptionManager from "./components/SubscriptionManager";
import SecuritySettings from "./components/SecuritySettings";
import AccountSettings from "./components/AccountSettings";
import AuthCallback from "./components/AuthCallback";
import EmailVerificationPage from "./components/EmailVerificationPage";
import PasswordResetPage from "./components/PasswordResetPage";
import InviteAccept from "./components/InviteAccept";
import QRPayPage from "./components/QRPayPage";
import QuickSplitCreate from "./components/QuickSplitCreate";
import QuickSplitHost from "./components/QuickSplitHost";
import QuickSplitPayPage from "./components/QuickSplitPayPage";
import QuickSplitSuccessPage from "./components/QuickSplitSuccessPage";
import WalletPage from "./components/WalletPage";
import PaymentMethodsPage from "./components/PaymentMethodsPage";
import SplitsPage from "./components/SplitsPage";
import LegacyGroupsPage from "./components/LegacyGroupsPage";

const AppContent = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [currentView, setCurrentView] = useState('landing');
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupForExpense, setGroupForExpense] = useState(null);

  // Auto-set to dashboard when auth resolves
  useEffect(() => {
    if (isAuthenticated && currentView === 'landing') {
      setCurrentView('dashboard');
    }
  }, [isAuthenticated]);

  // Open a specific group automatically after invite accept (cross-route hint)
  useEffect(() => {
    if (!isAuthenticated) return;
    try {
      const pendingGroupId = sessionStorage.getItem('open_group_id');
      if (pendingGroupId) {
        sessionStorage.removeItem('open_group_id');
        setSelectedGroupId(pendingGroupId);
        setCurrentView('groupDetail');
        return;
      }
      const wantsCreate = sessionStorage.getItem('legacy_create_group');
      if (wantsCreate) {
        sessionStorage.removeItem('legacy_create_group');
        setCurrentView('createGroup');
      }
    } catch { /* ignore */ }
  }, [isAuthenticated]);

  const handleGetStarted = () => {
    setCurrentView('dashboard');
  };

  const handleAddExpense = (groupId = null, groupData = null) => {
    setSelectedGroupId(groupId);
    setGroupForExpense(groupData);
    setCurrentView('addExpense');
  };

  const handleCreateGroup = () => {
    setCurrentView('createGroup');
  };

  const handleViewGroup = (groupId) => {
    setSelectedGroupId(groupId);
    setCurrentView('groupDetail');
  };

  const handleManageSubscriptions = () => {
    setCurrentView('subscriptions');
  };

  const handleSecuritySettings = () => {
    setCurrentView('security');
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setSelectedGroupId(null);
  };

  // Don't block on auth check — show landing page immediately
  // Auth resolves in the background and useEffect auto-transitions to dashboard

  // If not authenticated, show landing page or process OAuth
  if (!isAuthenticated) {
    return <Landing onGetStarted={handleGetStarted} />;
  }

  // If authenticated, show appropriate view
  const renderCurrentView = () => {
    switch (currentView) {
      case 'landing':
        return <Landing onGetStarted={handleGetStarted} />;
      case 'dashboard':
        return (
          <Dashboard 
            onAddExpense={handleAddExpense}
            onCreateGroup={handleCreateGroup}
            onViewGroup={handleViewGroup}
            onManageSubscriptions={handleManageSubscriptions}
            onSecuritySettings={handleSecuritySettings}
          />
        );
      case 'addExpense':
        return <AddExpense onBack={handleBackToDashboard} preSelectedGroupId={selectedGroupId} groupData={groupForExpense} />;
      case 'createGroup':
        return <CreateGroup onBack={handleBackToDashboard} />;
      case 'subscriptions':
        return <SubscriptionManager onBack={handleBackToDashboard} />;
      case 'security':
        return <SecuritySettings onBack={handleBackToDashboard} />;
      case 'groupDetail':
        return (
          <GroupDetail 
            key={selectedGroupId}
            groupId={selectedGroupId}
            onBack={handleBackToDashboard}
            onAddExpense={handleAddExpense}
          />
        );
      case 'inviteAccept':
        return (
          <InviteAccept 
            onGroupJoined={handleViewGroup}
          />
        );
      default:
        return (
          <Dashboard 
            onAddExpense={handleAddExpense}
            onCreateGroup={handleCreateGroup}
            onViewGroup={handleViewGroup}
            onManageSubscriptions={handleManageSubscriptions}
            onSecuritySettings={handleSecuritySettings}
          />
        );
    }
  };

  return renderCurrentView();
};

const AuthCallbackWrapper = () => {
  const { login } = useAuth();
  
  const handleAuthSuccess = (userData, accessToken) => {
    login(userData, accessToken);
  };
  
  return <AuthCallback onAuthSuccess={handleAuthSuccess} />;
};

const App = () => {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<AppContent />} />
            <Route path="/dashboard" element={<AppContent />} />
            <Route path="/onboarding" element={<AppContent />} />
            <Route path="/auth/verify" element={<EmailVerificationPage />} />
            <Route path="/auth/reset-password" element={<PasswordResetPage />} />
            <Route path="/auth/magic-link/verify" element={<AuthCallbackWrapper />} />
            <Route path="/auth/google/callback" element={<AuthCallbackWrapper />} />
            <Route path="/auth/apple/callback" element={<AuthCallbackWrapper />} />
            <Route path="/invite/accept" element={<InviteAccept />} />
            <Route path="/pay/qr/:requestId" element={<QRPayPage />} />
            <Route path="/pay/r/:requestId" element={<QRPayPage />} />
            <Route path="/quick-split/create" element={<QuickSplitCreate />} />
            <Route path="/quick-split/:sessionId/host" element={<QuickSplitHost />} />
            <Route path="/quick-split/:sessionId/success" element={<QuickSplitSuccessPage />} />
            <Route path="/quick-split/:sessionId" element={<QuickSplitPayPage />} />
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/payment-methods" element={<PaymentMethodsPage />} />
            <Route path="/bank-accounts" element={<PaymentMethodsPage />} />
            <Route path="/splits" element={<SplitsPage />} />
            {/* Legacy Groups page kept as internal back-compat for users with active groups (no longer surfaced in UI) */}
            <Route path="/legacy-groups" element={<LegacyGroupsPage />} />
            <Route path="/settings" element={<AccountSettings />} />
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
};

export default App;